import React, { useState } from 'react';
import Map, { Marker, Popup, NavigationControl, FullscreenControl } from 'react-map-gl/mapbox';
import { Box, Typography, Chip } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import 'mapbox-gl/dist/mapbox-gl.css';
import config from '../config';

const CATEGORY_COLORS = {
  restaurant: '#e91e63',
  hotel: '#9c27b0',
  attraction: '#2196f3',
  activity: '#4caf50',
  other: '#ff9800',
};

export default function MapView({ activities, onMarkerClick, height = 450 }) {
  const [popupInfo, setPopupInfo] = useState(null);

  const located = activities.filter((a) => a.location?.lat && a.location?.lng);

  if (located.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}
        sx={{ bgcolor: '#f0f0f0', borderRadius: 2 }}>
        <Typography color="text.secondary">No activities with locations yet.</Typography>
      </Box>
    );
  }

  const avgLat = located.reduce((s, a) => s + a.location.lat, 0) / located.length;
  const avgLng = located.reduce((s, a) => s + a.location.lng, 0) / located.length;

  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', height }}>
      <Map
        mapboxAccessToken={config.mapboxToken}
        initialViewState={{ latitude: avgLat, longitude: avgLng, zoom: located.length === 1 ? 13 : 9 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={config.mapStyle}
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />

        {located.map((activity) => (
          <Marker
            key={activity.activityId}
            latitude={activity.location.lat}
            longitude={activity.location.lng}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setPopupInfo(activity);
            }}
          >
            <Box
              sx={{
                bgcolor: CATEGORY_COLORS[activity.category] || '#ff9800',
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: 2,
                '&:hover': { transform: 'rotate(-45deg) scale(1.2)' },
                transition: 'transform 0.15s',
              }}
            >
              <PlaceIcon sx={{ transform: 'rotate(45deg)', color: 'white', fontSize: 18 }} />
            </Box>
          </Marker>
        ))}

        {popupInfo && (
          <Popup
            latitude={popupInfo.location.lat}
            longitude={popupInfo.location.lng}
            anchor="top"
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
          >
            <Box sx={{ p: 0.5, minWidth: 150 }}>
              <Typography fontWeight="bold" variant="body2">{popupInfo.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {popupInfo.location.address}
              </Typography>
              <Chip
                label={popupInfo.category}
                size="small"
                sx={{ mt: 0.5, bgcolor: CATEGORY_COLORS[popupInfo.category], color: 'white', fontSize: 11 }}
                onClick={() => { onMarkerClick && onMarkerClick(popupInfo); setPopupInfo(null); }}
              />
            </Box>
          </Popup>
        )}
      </Map>
    </Box>
  );
}
